import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DayPicker, type DayPickerProps } from 'react-day-picker'
import 'react-day-picker/style.css'
import { cn } from '@/lib/utils.js'

export type CalendarProps = DayPickerProps & { className?: string }

/**
 * shadcn-style Calendar wrapper around react-day-picker v10. The token-themed
 * class overrides re-skin the picker against the Veritact accent.
 */
export const Calendar = ({ className, classNames, ...props }: CalendarProps): React.ReactElement => (
  <DayPicker
    className={cn('p-3', className)}
    classNames={{
      root: 'rdp text-foreground',
      months: 'flex flex-col space-y-4',
      month: 'space-y-4',
      caption_label: 'text-sm font-semibold',
      nav: 'flex items-center gap-1',
      button_previous: cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-accent-foreground',
      ),
      button_next: cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-accent-foreground',
      ),
      month_grid: 'w-full border-collapse',
      weekdays: 'flex',
      weekday: 'w-9 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold text-center',
      week: 'flex w-full mt-1',
      // react-day-picker v10 puts aria-selected on this <td> (not the button),
      // so the "selected" styling has to be targeted from here via an arbitrary
      // child variant. Style the inner button when the cell is selected.
      day: cn(
        'h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20',
        '[&[aria-selected=true]>button]:bg-primary',
        '[&[aria-selected=true]>button]:text-primary-foreground',
        '[&[aria-selected=true]>button]:font-semibold',
        '[&[aria-selected=true]>button]:shadow-[0_4px_12px_-2px_oklch(38%_0.155_266/0.45)]',
        '[&[aria-selected=true]>button:hover]:bg-primary',
        '[&[aria-selected=true]>button:hover]:text-primary-foreground',
      ),
      day_button:
        'inline-flex h-9 w-9 items-center justify-center rounded-full font-medium hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-foreground',
      today: 'font-bold underline underline-offset-4 decoration-primary/40',
      outside: 'text-muted-foreground/50',
      disabled: 'text-muted-foreground/40 cursor-not-allowed',
      hidden: 'invisible',
      ...classNames,
    }}
    components={{
      Chevron: ({ orientation }) =>
        orientation === 'left' ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />,
    }}
    {...props}
  />
)
Calendar.displayName = 'Calendar'
